import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, MapPin, Buildings, Star, Phone, Clock, GraduationCap,
  CheckCircle, CaretRight, CaretDown, User, CalendarDots,
  ShieldCheck, Users, FileText, Syringe, Prohibit,
} from "@phosphor-icons/react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { DateTimeField } from "@/components/datetime";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatTime12, formatTimeRange12 } from "@/lib/utils";
import BrandedPageLoader from "@/components/BrandedPageLoader";

const todayInputValue = () => new Date().toISOString().slice(0, 10);
const maxDateValue = (windowDays) => {
  if (!windowDays || windowDays <= 0) return "";
  const d = new Date();
  d.setDate(d.getDate() + windowDays);
  return d.toISOString().slice(0, 10);
};
const DAY_ORDER = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function sessionSlotsForBookingType(session, bookingType) {
  if (!session || session.is_past) return 0;
  if (bookingType === "follow_up") {
    return session.follow_up_slots_left ?? session.bookable_slots_left ?? session.slots_left ?? 0;
  }
  return session.new_patient_slots_left ?? session.bookable_slots_left ?? session.slots_left ?? 0;
}

function timingSummary(schedule = {}) {
  const active = DAY_ORDER.filter((day) => schedule?.[day]?.enabled);
  if (!active.length) return "Unavailable";
  return active
    .map((day) => {
      const sessions = schedule[day]?.sessions || [];
      if (sessions.length > 1) {
        return `${day} ${sessions.map((s) => `${s.label}: ${formatTime12(s.start_time)}–${formatTime12(s.end_time)}`).join(", ")}`;
      }
      return `${day} ${formatTime12(schedule[day].start_time)}–${formatTime12(schedule[day].end_time)}`;
    })
    .join(" · ");
}

function LocationCard({ location, isSelected, onSelect }) {
  const [expanded, setExpanded] = useState(false);
  const activeDays = DAY_ORDER.filter((d) => location.working_schedule?.[d]?.enabled);

  return (
    <div
      className={`rounded-2xl border transition-all cursor-pointer ${
        isSelected
          ? "border-primary bg-primary/5 shadow-sm"
          : "border-border hover:border-primary/30 hover:bg-muted/30"
      }`}
      onClick={() => onSelect(location.id)}
    >
      <div className="p-4 flex items-start gap-3">
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
          isSelected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
        }`}>
          <MapPin size={18} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-display font-bold text-foreground text-sm truncate">{location.clinic_name}</span>
            {location.is_default && (
              <span className="text-[9px] uppercase tracking-wider font-bold bg-primary/10 text-primary px-1.5 py-0.5 rounded-full shrink-0">Default</span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{location.clinic_address}</p>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5">
            {location.consultation_fee > 0 && (
              <span className="text-xs font-bold text-accent">₹{location.consultation_fee}</span>
            )}
            <span className="text-xs text-muted-foreground">{activeDays.length} active day{activeDays.length !== 1 ? "s" : ""}</span>
            <span className="text-xs text-muted-foreground">{location.max_daily_appointments || 30}/day</span>
          </div>
        </div>
        <div className="shrink-0 text-muted-foreground mt-1">
          {isSelected ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
        </div>
      </div>

      {isSelected && (
        <div className="px-4 pb-4 pt-0">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {expanded ? "Hide details" : "Show schedule"}
          </button>
          {expanded && (
            <div className="mt-2 rounded-xl bg-background border border-border p-3 space-y-1">
              {DAY_ORDER.map((day) => {
                const daySched = location.working_schedule?.[day];
                if (!daySched?.enabled) return null;
                const sessions = daySched.sessions || [];
                return (
                  <div key={day} className="flex items-center gap-2 text-xs">
                    <span className="font-bold text-foreground w-8">{day}</span>
                    <span className="text-muted-foreground">
                      {sessions.map((s) => `${formatTime12(s.start_time)}–${formatTime12(s.end_time)}`).join(", ")}
                    </span>
                  </div>
                );
              })}
              {DAY_ORDER.filter((d) => location.working_schedule?.[d]?.enabled).length === 0 && (
                <p className="text-xs text-muted-foreground">No active days</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SessionSlot({ session, isSelected, onSelect, bookingType }) {
  const bookableLeft = sessionSlotsForBookingType(session, bookingType);
  const isSelectable = session.available && bookableLeft > 0;
  const statusLabel = session.is_past ? "Ended" : session.starts_soon ? "Soon" : session.has_started ? "Live" : bookableLeft <= 0 ? (session.unavailable_reason || "Full") : `${bookableLeft} left`;

  return (
    <button
      type="button"
      disabled={!isSelectable}
      onClick={onSelect}
      className={`w-full text-left rounded-2xl border p-4 transition-all ${
        isSelected
          ? "border-primary bg-primary/5 ring-1 ring-primary/20"
          : "border-border bg-card hover:bg-muted/30"
      } ${!isSelectable ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <span className="font-display font-bold text-foreground text-sm">{session.label}</span>
          <span className="text-xs text-muted-foreground ml-2">{formatTime12(session.start_time)} – {formatTime12(session.end_time)}</span>
        </div>
        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
          session.is_past ? "bg-muted text-muted-foreground" :
          bookableLeft <= 0 ? "bg-orange-50 text-orange-600" :
          isSelected ? "bg-primary text-primary-foreground" :
          "bg-primary/10 text-primary"
        }`}>
          {statusLabel}
        </span>
      </div>
      {isSelected && (
        <div className="mt-3 pt-3 border-t border-border/50 flex gap-4 text-xs text-muted-foreground">
          {session.new_patient_limit > 0 && (
            <span>New: {session.new_patient_booked || 0}/{session.new_patient_limit}</span>
          )}
          {session.follow_up_limit > 0 && (
            <span>F/U: {session.follow_up_booked || 0}/{session.follow_up_limit}</span>
          )}
          <span>Total: {session.booked_count || 0}/{session.max_appointments}</span>
        </div>
      )}
    </button>
  );
}

export default function DoctorProfileBooking() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [doctor, setDoctor] = useState(null);
  const [reason, setReason] = useState("");
  const [queueDate, setQueueDate] = useState(todayInputValue());
  const [availability, setAvailability] = useState(null);
  const [selectedSessionId, setSelectedSessionId] = useState("");
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(null);
  const [followUpInfo, setFollowUpInfo] = useState(null);
  const [dependents, setDependents] = useState([]);
  const [selectedDependent, setSelectedDependent] = useState(null);
  const [procedures, setProcedures] = useState([]);
  const [bookingTab, setBookingTab] = useState("consultation");
  const [selectedBookingType, setSelectedBookingType] = useState("new");
  const [procRequestModal, setProcRequestModal] = useState({ open: false, procedure: null });
  const [procDate, setProcDate] = useState(todayInputValue());
  const [procNotes, setProcNotes] = useState("");
  const [procBusy, setProcBusy] = useState(false);
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState(null);

  useEffect(() => {
    api.get(`/doctors/${id}`).then(({ data }) => setDoctor(data)).catch(() => navigate("/doctors"));
  }, [id, navigate]);

  useEffect(() => {
    if (!doctor) return;
    api.get(`/doctors/${id}/locations`).then(({ data }) => {
      const locs = data.locations || [];
      setLocations(locs);
      if (locs.length > 0) {
        const def = locs.find((l) => l.is_default) || locs[0];
        setSelectedLocationId(def.id);
      }
    }).catch(() => {});
  }, [doctor, id]);

  useEffect(() => {
    if (!doctor || !user || user.role !== "patient") return;
    const params = selectedDependent?.id ? { dependent_id: selectedDependent.id } : {};
    api.get(`/appointments/follow-up-eligible/${id}`, { params }).then(({ data }) => {
      setFollowUpInfo(data);
      setSelectedBookingType(data?.eligible ? "follow_up" : "new");
    }).catch(() => setFollowUpInfo(null));
  }, [doctor, id, user, selectedDependent]);

  useEffect(() => {
    if (user?.role === "patient") {
      api.get("/dependents").then(({ data }) => setDependents(data.dependents || [])).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    if (doctor?.procedures_enabled) {
      api.get(`/procedures/doctor/${id}`).then(({ data }) => setProcedures(data.procedures || [])).catch(() => {});
    }
  }, [doctor, id]);

  useEffect(() => {
    if (!doctor || !queueDate) return;
    setAvailabilityLoading(true);
    const params = { queue_date: queueDate };
    if (selectedLocationId) params.location_id = selectedLocationId;
    api
      .get(`/doctors/${id}/availability`, { params })
      .then(({ data }) => {
        setAvailability(data);
        setSelectedSessionId(
          (data.sessions || []).find((s) => s.available && sessionSlotsForBookingType(s, selectedBookingType) > 0)?.id || ""
        );
        setSelectedSlot(null);
      })
      .catch(() => setAvailability(null))
      .finally(() => setAvailabilityLoading(false));
  }, [doctor, id, queueDate, selectedBookingType, selectedLocationId]);

  const onBook = async (e) => {
    e.preventDefault();
    if (authLoading) {
      toast.message("Checking your account. Please try again in a moment.");
      return;
    }
    if (!user) {
      toast.message("Please sign in as a patient to book.");
      localStorage.setItem("redirectAfterLogin", `/doctors/${id}`);
      navigate(`/login?next=/doctors/${id}`);
      return;
    }
    if (user.role !== "patient") {
      toast.error("Only patients can book appointments.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/appointments", {
        doctor_id: id, reason, queue_date: queueDate,
        dependent_id: selectedDependent?.id || null,
        queue_session_id: selectedSessionId || null,
        booking_type: selectedBookingType,
        location_id: selectedLocationId || null,
        slot_start_time: null,
        slot_end_time: null,
        slot_label: null,
      });
      setSuccess(data);
      setReason("");
      const refreshParams = { queue_date: queueDate };
      if (selectedLocationId) refreshParams.location_id = selectedLocationId;
      const refreshed = await api.get(`/doctors/${id}/availability`, { params: refreshParams });
      setAvailability(refreshed.data);
      setSelectedSessionId(
        (refreshed.data.sessions || []).find((s) => s.available && sessionSlotsForBookingType(s, selectedBookingType) > 0)?.id || ""
      );
      setSelectedSlot(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Could not book");
    } finally {
      setBusy(false);
    }
  };

  const onProcRequest = async (e) => {
    e.preventDefault();
    setProcBusy(true);
    try {
      await api.post("/procedure-requests", {
        doctor_id: id,
        procedure_id: procRequestModal.procedure.id,
        scheduled_date: procDate,
        notes: procNotes,
      });
      toast.success("Procedure request sent to the doctor.");
      setProcRequestModal({ open: false, procedure: null });
      setProcNotes("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Could not send request");
    } finally {
      setProcBusy(false);
    }
  };

  const selectedLocation = useMemo(
    () => locations.find((l) => l.id === selectedLocationId),
    [locations, selectedLocationId],
  );

  const effectiveFee = useMemo(() => {
    if (selectedBookingType === "follow_up" && followUpInfo?.eligible) return followUpInfo.follow_up_fee;
    if (selectedLocation?.consultation_fee != null && selectedLocation.consultation_fee > 0) return selectedLocation.consultation_fee;
    return doctor?.consultation_fee || 0;
  }, [selectedBookingType, followUpInfo, selectedLocation, doctor]);
  const selectedSession = availability?.sessions?.find((s) => s.id === selectedSessionId);
  const needsSlot = false;
  const slotValid = true;

  if (!doctor) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <BrandedPageLoader message="Loading doctor" subtext="Checking profile and availability" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <Link to="/doctors" className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground text-sm mb-5 sm:mb-6">
          <ArrowLeft size={16} weight="bold" /> Back to all doctors
        </Link>

        <div className="flex flex-col lg:flex-row gap-6 sm:gap-8">

          {/* ── Left Column: Doctor Profile & Locations ── */}
          <div className="flex-1 space-y-6">

            {/* Doctor Header Card */}
            <div className="bg-card border border-border rounded-3xl overflow-hidden shadow-sm">
              <div className="p-5 sm:p-7 border-b border-border bg-muted/25">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5">
                  {doctor.photo_url && (
                    <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-3xl overflow-hidden border border-border bg-card shadow-sm shrink-0">
                      <img src={doctor.photo_url} alt={doctor.name} className="w-full h-full object-cover" data-testid="doctor-photo" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">{doctor.specialty}</div>
                    <h1 className="font-display font-bold text-foreground text-2xl sm:text-3xl lg:text-4xl tracking-tight mt-1 break-words" data-testid="doctor-name">
                      {doctor.name}
                      {doctor.verified && <CheckCircle size={20} weight="fill" className="text-blue-500 inline-block ml-1.5" />}
                    </h1>
                    {doctor.clinic_name && (
                      <div className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground">
                        <Buildings size={15} weight="duotone" className="shrink-0" />
                        <span className="truncate">{doctor.clinic_name}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-5 sm:p-7">
                {/* Badges row */}
                <div className="flex flex-wrap gap-2 mb-5">
                  {doctor.qualifications && (
                    <Badge variant="secondary" className="rounded-full inline-flex items-center gap-1.5">
                      <GraduationCap size={14} weight="duotone" /> {doctor.qualifications}
                    </Badge>
                  )}
                  {doctor.show_experience !== false && (
                    <Badge variant="secondary" className="rounded-full inline-flex items-center gap-1.5">
                      <Star size={14} weight="duotone" /> {doctor.years_experience}+ yrs
                    </Badge>
                  )}
                  {doctor.show_fee !== false && (
                    <Badge className="rounded-full bg-accent text-accent-foreground font-display font-bold">
                      ₹{doctor.consultation_fee}
                    </Badge>
                  )}
                </div>

                {/* Bio */}
                {doctor.bio && (
                  <p className="text-muted-foreground leading-relaxed text-sm sm:text-base">{doctor.bio}</p>
                )}

                {/* Quick Info Grid */}
                <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="rounded-2xl border border-border bg-background p-3">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Clinic</div>
                    <div className="text-sm font-semibold text-foreground mt-1 truncate">{doctor.clinic_name}</div>
                  </div>
                  <div className="rounded-2xl border border-border bg-background p-3">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Phone</div>
                    <div className="text-sm font-semibold text-foreground mt-1">{doctor.phone || "—"}</div>
                  </div>
                  <div className="rounded-2xl border border-border bg-background p-3">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Flow</div>
                    <div className="text-sm font-semibold text-foreground mt-1 truncate">Token + live queue</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Procedures */}
            {doctor.procedures_enabled && procedures.filter((p) => p.active !== false).length > 0 && (
              <div className="bg-card border border-border rounded-3xl p-5 sm:p-7">
                <div className="flex items-center gap-2 mb-4">
                  <Syringe size={20} weight="duotone" className="text-accent" />
                  <h3 className="font-display font-bold text-foreground text-xl">Procedures & Services</h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {procedures.filter((p) => p.active !== false).map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setProcRequestModal({ open: true, procedure: p })}
                      className="rounded-2xl border border-border bg-background p-4 text-left hover:bg-muted/40 transition-colors"
                    >
                      <div className="font-semibold text-foreground text-sm">{p.name}</div>
                      {p.description && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.description}</div>}
                      <div className="flex flex-wrap gap-2 mt-2">
                        {p.price != null && <span className="text-xs font-bold text-accent">₹{p.price}</span>}
                        {p.price_min != null && p.price_max != null && <span className="text-xs font-bold text-accent">₹{p.price_min}–{p.price_max}</span>}
                        <span className="text-xs text-muted-foreground">{p.duration_minutes} min</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Locations Section */}
            <div className="bg-card border border-border rounded-3xl p-5 sm:p-7">
              <div className="flex items-center gap-2 mb-4">
                <Buildings size={20} weight="duotone" className="text-primary" />
                <h3 className="font-display font-bold text-foreground text-xl">Clinic Locations</h3>
                {locations.length > 0 && (
                  <span className="text-xs text-muted-foreground ml-auto">{locations.length} location{locations.length !== 1 ? "s" : ""}</span>
                )}
              </div>
              <div className="space-y-3">
                {locations.map((loc) => (
                  <LocationCard
                    key={loc.id}
                    location={loc}
                    isSelected={selectedLocationId === loc.id}
                    onSelect={setSelectedLocationId}
                  />
                ))}
                {locations.length === 0 && (
                  <div className="rounded-2xl border border-dashed border-border p-6 text-center text-muted-foreground text-sm">
                    No locations available.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Right Column: Booking Sidebar ── */}
          <div className="w-full lg:w-[420px] shrink-0">
            <div className="bg-card border border-border rounded-3xl shadow-xl overflow-hidden lg:sticky lg:top-24">

              {/* Sidebar Header */}
              <div className="bg-primary p-5 sm:p-6 relative overflow-hidden">
                <div className="absolute inset-0 grain-overlay opacity-20" />
                <div className="relative">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-primary-foreground/60 mb-1">Find care near you</div>
                  <h2 className="font-display font-bold text-primary-foreground text-2xl sm:text-3xl tracking-tight">Book Appointment</h2>
                  <p className="text-primary-foreground/70 text-sm mt-1">Book your appointment time</p>
                </div>
              </div>

              <form onSubmit={onBook} className="p-5 sm:p-6 space-y-5" data-testid="booking-form">

                {/* Patient Selection */}
                {dependents.length > 0 && (
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-2 block">Booking for</Label>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setSelectedDependent(null)}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold border transition-all ${
                          !selectedDependent
                            ? "bg-primary text-primary-foreground border-primary shadow-sm"
                            : "bg-background text-muted-foreground border-border hover:border-primary/30"
                        }`}
                      >
                        <User size={14} weight="duotone" /> Me
                      </button>
                      {dependents.map((dep) => (
                        <button
                          key={dep.id}
                          type="button"
                          onClick={() => setSelectedDependent(dep)}
                          className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold border transition-all ${
                            selectedDependent?.id === dep.id
                              ? "bg-primary text-primary-foreground border-primary shadow-sm"
                              : "bg-background text-muted-foreground border-border hover:border-primary/30"
                          }`}
                        >
                          <Users size={14} weight="duotone" /> {dep.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Date Selection */}
                <div>
                  <Label htmlFor="booking-date" className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-2 block">
                    <CalendarDots size={14} weight="duotone" className="inline mr-1.5" />
                    Appointment date
                  </Label>
                  <DateTimeField
                    mode="date"
                    value={queueDate}
                    onChange={setQueueDate}
                    minDate={todayInputValue()}
                    maxDate={maxDateValue(doctor?.booking_window_days)}
                    placeholder="Choose appointment date"
                    data-testid="booking-date"
                  />
                </div>

                {/* Availability Status */}
                <div className="rounded-2xl border border-border bg-background p-4">
                  {availabilityLoading ? (
                    <div className="flex items-center gap-2 text-muted-foreground text-sm">
                      <div className="w-4 h-4 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
                      Checking slots…
                    </div>
                  ) : availability ? (
                    !availability.clinic_open ? (
                      <div className="flex items-center gap-2">
                        <Prohibit size={16} className="text-destructive shrink-0" weight="duotone" />
                        <span className="text-destructive font-semibold text-sm">Clinic is closed</span>
                      </div>
                    ) : availability.is_blocked_date ? (
                      <div className="flex items-center gap-2">
                        <Prohibit size={16} className="text-destructive shrink-0" weight="duotone" />
                        <span className="text-destructive font-semibold text-sm">{availability.message || "Doctor unavailable"}</span>
                      </div>
                    ) : availability.is_working_day ? (
                      availability.available ? (
                        <div>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-green-500" />
                            <span className="text-primary font-bold text-sm">
                              {availability.sessions.reduce((sum, s) => sum + sessionSlotsForBookingType(s, selectedBookingType), 0)} {selectedBookingType === "follow_up" ? "follow-up" : "new patient"} slot{availability.sessions.reduce((sum, s) => sum + sessionSlotsForBookingType(s, selectedBookingType), 0) !== 1 ? "s" : ""}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatTime12(availability.start_time || doctor.start_time)} – {formatTime12(availability.end_time || doctor.end_time)}
                            {availability.custom_timing_note ? ` · ${availability.custom_timing_note}` : ""}
                          </p>
                          {followUpInfo?.eligible && (
                            <div className="mt-2 rounded-xl bg-green-50 border border-green-200 px-3 py-2 text-xs text-green-800 font-semibold">
                              ✓ {followUpInfo.remaining_visits} follow-up visit{followUpInfo.remaining_visits !== 1 ? "s" : ""} remaining
                            </div>
                          )}
                        </div>
                      ) : (
                        <div>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-orange-400" />
                            <span className="text-orange-600 font-semibold text-sm">No slots available</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">{availability.message || "Try another date."}</p>
                        </div>
                      )
                    ) : (
                      <div>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-muted-foreground/40" />
                          <span className="text-muted-foreground font-semibold text-sm">Not a working day</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{availability.message || "Try another date."}</p>
                      </div>
                    )
                  ) : (
                    <span className="text-muted-foreground text-sm">Select a date to check availability.</span>
                  )}
                </div>

                {/* Session Selection */}
                {availability?.sessions && availability.sessions.length > 0 && (
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-2 block">
                      Available sessions
                    </Label>
                    <div className="space-y-2">
                      {availability.sessions.map((session) => {
                        const isSlotBased = false;
                        return (
                          <div key={session.id} className={`rounded-2xl border p-3 transition-all ${selectedSessionId === session.id ? "border-primary bg-primary/5 ring-1 ring-primary/20" : "border-border"}`}>
                            <div className="flex items-center justify-between mb-2">
                              <div>
                                <span className="font-display font-bold text-foreground text-sm">{session.label}</span>
                                <span className="text-xs text-muted-foreground ml-2">{session.start_time} – {session.end_time}</span>
                              </div>
                              <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{isSlotBased ? "SLOTS" : "TOKENS"}</span>
                            </div>
                            {isSlotBased ? (
                              <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                                {session.slots?.map((slot) => (
                                  <button
                                    key={`${slot.start_time}-${slot.end_time}`}
                                    type="button"
                                    disabled={!slot.available}
                                    onClick={() => { setSelectedSessionId(session.id); setSelectedSlot(slot); }}
                                    className={`text-xs py-2 rounded-lg border font-semibold transition-all ${
                                      selectedSessionId === session.id && selectedSlot?.start_time === slot.start_time
                                        ? "bg-primary text-primary-foreground border-primary"
                                        : slot.available
                                          ? "bg-background text-foreground border-border hover:border-primary/40"
                                          : "bg-muted/50 text-muted-foreground/40 border-border cursor-not-allowed line-through"
                                    }`}
                                  >
                                    {slot.short_label || slot.start_time?.slice(0, 5)}
                                    {slot.slots_left != null && slot.slots_left < slot.capacity && (
                                      <span className="block text-[9px] opacity-60">{slot.slots_left} left</span>
                                    )}
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-muted-foreground">
                                  {session.bookable_slots_left != null ? `${session.bookable_slots_left} slot${session.bookable_slots_left !== 1 ? "s" : ""} left` : `${session.slots_left || 0} left`}
                                </span>
                                <button
                                  type="button"
                                  disabled={!session.available}
                                  onClick={() => { setSelectedSessionId(session.id); setSelectedSlot(null); }}
                                  className={`text-xs font-bold px-3 py-1.5 rounded-full transition-all ${
                                    selectedSessionId === session.id
                                      ? "bg-primary text-primary-foreground"
                                      : session.available
                                        ? "bg-primary/10 text-primary hover:bg-primary/20"
                                        : "bg-muted text-muted-foreground cursor-not-allowed"
                                  }`}
                                >
                                  {session.available ? "Select" : session.unavailable_reason || "Full"}
                                </button>
                              </div>
                            )}
                            {selectedSessionId === session.id && (
                              <div className="mt-2 pt-2 border-t border-border/50 flex gap-4 text-xs text-muted-foreground">
                                {session.new_patient_limit > 0 && (
                                  <span>New: {session.new_patient_booked || 0}/{session.new_patient_limit}</span>
                                )}
                                {session.follow_up_limit > 0 && (
                                  <span>F/U: {session.follow_up_booked || 0}/{session.follow_up_limit}</span>
                                )}
                                <span>Total: {session.booked_count || 0}/{session.max_appointments}</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-[10px] text-muted-foreground text-center mt-2 italic">First come, first served (FCFS) basis</p>
                  </div>
                )}

                {/* Booking Type Toggle */}
                {followUpInfo?.eligible && (
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-2 block">Visit type</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setSelectedBookingType("new")}
                        className={`rounded-2xl border px-3 py-3 text-sm font-semibold transition-all ${
                          selectedBookingType === "new"
                            ? "border-primary bg-primary/5 text-primary"
                            : "border-border bg-background text-muted-foreground hover:border-primary/30"
                        }`}
                      >
                        New visit · ₹{doctor.consultation_fee}
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedBookingType("follow_up")}
                        className={`rounded-2xl border px-3 py-3 text-sm font-semibold transition-all ${
                          selectedBookingType === "follow_up"
                            ? "border-primary bg-primary/5 text-primary"
                            : "border-border bg-background text-muted-foreground hover:border-primary/30"
                        }`}
                      >
                        Follow-up · ₹{followUpInfo.follow_up_fee}
                      </button>
                    </div>
                  </div>
                )}

                {/* Reason */}
                <div>
                  <Label htmlFor="reason" className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-2 block">Reason for visit</Label>
                  <Textarea
                    id="reason"
                    required
                    minLength={2}
                    maxLength={300}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                    className="resize-none rounded-2xl"
                    placeholder="e.g. Persistent cough for 4 days"
                    data-testid="booking-reason"
                  />
                </div>

                {/* CTA */}
                <div className="space-y-3 pt-2">
                  <Button
                    type="submit"
                    disabled={busy || authLoading || availabilityLoading || !availability?.available || !selectedSessionId || !slotValid}
                    className="w-full h-13 rounded-full bg-primary text-primary-foreground hover:bg-[#1a3025] text-base font-bold shadow-lg shadow-primary/20"
                    data-testid="booking-submit"
                  >
                    {authLoading ? "Checking account…" : busy ? "Reserving…" : followUpInfo?.eligible && selectedBookingType === "follow_up"
                      ? `Book follow-up · ₹${followUpInfo.follow_up_fee}`
                      : needsSlot
                        ? `Book slot · ₹${effectiveFee}`
                        : doctor.show_fee !== false ? `Book token · ₹${effectiveFee}` : "Book token"}
                  </Button>
                  <p className="text-[11px] font-bold uppercase tracking-tighter text-muted-foreground text-center">
                    No payment required today
                  </p>
                </div>

                {!user && (
                  <p className="text-xs text-muted-foreground text-center">
                    Sign in or create an account to complete booking.
                  </p>
                )}
              </form>

              {/* Trust Footer */}
              <div className="bg-muted/50 px-5 sm:px-6 py-4 border-t border-border flex gap-3">
                <ShieldCheck size={18} weight="duotone" className="text-primary shrink-0 mt-0.5" />
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  All appointments follow strict clinical safety protocols and sanitized environments.
                </p>
              </div>
            </div>
          </div>

        </div>
      </main>

      {/* Procedure Request Modal */}
      <Dialog open={procRequestModal.open} onOpenChange={(open) => setProcRequestModal({ open, procedure: open ? procRequestModal.procedure : null })}>
        <DialogContent className="rounded-3xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Request procedure</DialogTitle>
            <DialogDescription>
              {procRequestModal.procedure?.name} · {procRequestModal.procedure?.duration_minutes} min
              {procRequestModal.procedure?.price != null && ` · ₹${procRequestModal.procedure.price}`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onProcRequest} className="space-y-4">
            <div>
              <Label htmlFor="proc-date">Preferred date</Label>
              <DateTimeField
                mode="date"
                value={procDate}
                onChange={setProcDate}
                minDate={todayInputValue()}
                placeholder="Choose preferred date"
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="proc-notes">Notes</Label>
              <Textarea id="proc-notes" value={procNotes} onChange={(e) => setProcNotes(e.target.value)} rows={3} className="mt-2 resize-none" placeholder="Any notes for the doctor…" />
            </div>
            <Button type="submit" disabled={procBusy} className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-[#1a3025]">
              {procBusy ? "Sending…" : "Send request"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Success Dialog */}
      <Dialog open={!!success} onOpenChange={() => setSuccess(null)}>
        <DialogContent className="rounded-3xl max-w-sm text-center">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Appointment Confirmed</DialogTitle>
          </DialogHeader>
              {success && (
            <div className="py-4">
              {success.slot_start_time ? (
                <>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Your appointment time</div>
                  <div className="font-token text-5xl text-primary" data-testid="success-slot">
                    {success.slot_label || `${success.slot_start_time?.slice(0, 5)} – ${success.slot_end_time?.slice(0, 5)}`}
                  </div>
                </>
              ) : (
                <>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Your token number</div>
                  <div className="font-token text-7xl text-primary" data-testid="success-token">
                    #{String(success.token_number).padStart(2, "0")}
                  </div>
                </>
              )}
              <p className="text-sm text-muted-foreground mt-3">
                {success.queue_session_label} · {success.queue_date}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Dr {doctor.name}
              </p>
              {success.consultation_fee > 0 && (
                <p className="text-sm text-accent font-bold mt-2">₹{success.consultation_fee}</p>
              )}
            </div>
          )}
          <Button onClick={() => { setSuccess(null); navigate("/appointments"); }} className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-[#1a3025]">
            View my appointments
          </Button>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
